/**
 * Score calculation utilities — ported verbatim from the backend's
 * `src/game/utils/score.util.ts`. All scoring logic now runs entirely on the frontend.
 */

/** Full `maxScore` if the round's maze was solved, 0 otherwise (no partial credit). */
export function calculateRoundScore(reachedEnd: boolean, maxScore: number): number {
  return reachedEnd ? maxScore : 0;
}

/** Linear percentage of remaining time, floored to an integer. */
export function calculateTimeBonus(
  remainingMs: number,
  timeLimitSeconds: number,
  maxTimeBonus: number,
): number {
  if (remainingMs <= 0) {
    return 0;
  }
  const remainingSeconds = remainingMs / 1000;
  const pct = remainingSeconds / timeLimitSeconds;
  return Math.floor(pct * maxTimeBonus);
}

export interface RoundResult {
  reachedEnd: boolean;
  maxScore: number;
  skipped?: boolean;
}

export interface AggregateScoreParams {
  rounds: RoundResult[];
  totalRounds: number;
  timeLimitSeconds: number;
  maxTimeBonus: number;
  remainingMsAtLastRoundEnd: number;
  /** When the timer feature flag is off, the time bonus is forced to 0. */
  timerEnabled: boolean;
}

export interface AggregateScoreResult {
  roundsScore: number;
  timeBonus: number;
  totalScore: number;
}

/**
 * Matches the backend's `finalizeAndCommit` aggregation rules:
 * - a skipped round always scores 0 regardless of `reachedEnd`
 * - the time bonus is only awarded if every round was played and the last one reached the end
 */
export function aggregateSessionScore(params: AggregateScoreParams): AggregateScoreResult {
  const roundsScore = params.rounds.reduce(
    (sum, round) =>
      sum + (round.skipped ? 0 : calculateRoundScore(round.reachedEnd, round.maxScore)),
    0,
  );

  const isFullyEnded =
    params.rounds.length === params.totalRounds &&
    params.rounds[params.rounds.length - 1]?.reachedEnd === true;

  const timeBonus =
    isFullyEnded && params.timerEnabled
      ? calculateTimeBonus(
          params.remainingMsAtLastRoundEnd,
          params.timeLimitSeconds,
          params.maxTimeBonus,
        )
      : 0;

  return { roundsScore, timeBonus, totalScore: roundsScore + timeBonus };
}
